<script lang="ts">
	import { tick } from 'svelte';
	import { Keyboard } from '@lucide/svelte';

	// Shown on "?" (outside text inputs). Wired from +layout.svelte alongside
	// CommandPalette, same bindable-open pattern.
	let { open = $bindable(false) }: { open?: boolean } = $props();

	const shortcuts: { keys: string; desc: string }[] = [
		{ keys: 'Ctrl/Cmd + K', desc: 'Ouvrir la palette de commandes' },
		{ keys: '?', desc: 'Afficher cette liste de raccourcis' },
		{ keys: 'Échap', desc: 'Fermer la palette, cette liste, ou une confirmation en cours' },
		{ keys: '↑ / ↓', desc: 'Naviguer dans la palette de commandes' },
		{ keys: 'Entrée', desc: 'Valider le résultat sélectionné dans la palette' },
		{
			keys: '← / →',
			desc: "Changer d'onglet (Aperçu, Joueurs, Console…) quand un onglet a le focus"
		}
	];

	function close() {
		open = false;
	}

	function onKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') {
			e.preventDefault();
			close();
		}
	}

	// Bug réel trouvé en testant pour de vrai (pas en relisant le code) :
	// onkeydown ci-dessous est posé sur .modal, mais rien ne lui donnait le
	// focus à l'ouverture -- Échap n'atteignait donc jamais ce gestionnaire
	// (le focus restait sur l'élément qui avait le focus avant l'ouverture,
	// typiquement <body>), alors que la liste elle-même annonce "Échap :
	// fermer... cette liste". CommandPalette.svelte fait déjà .focus() sur
	// son input à l'ouverture pour la même raison ; même correctif ici.
	let modalEl = $state<HTMLDivElement | undefined>(undefined);
	$effect(() => {
		if (open) tick().then(() => modalEl?.focus());
	});
</script>

{#if open}
	<div class="backdrop" onclick={close} role="presentation">
		<div
			class="modal"
			role="dialog"
			aria-modal="true"
			aria-label="Raccourcis clavier"
			onclick={(e) => e.stopPropagation()}
			onkeydown={onKeydown}
			bind:this={modalEl}
			tabindex="-1"
		>
			<h2><Keyboard size={16} /> Raccourcis clavier</h2>
			<ul>
				{#each shortcuts as s}
					<li>
						<kbd>{s.keys}</kbd>
						<span>{s.desc}</span>
					</li>
				{/each}
			</ul>
			<button class="close" onclick={close}>Fermer</button>
		</div>
	</div>
{/if}

<style>
	.backdrop {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.4);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 200;
		animation: fade var(--dur-fast) var(--ease-out);
	}
	.modal {
		width: min(420px, 92vw);
		background: var(--surface-raised);
		border: 1px solid var(--border);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-lg);
		padding: var(--space-5);
		animation: rise var(--dur-base) var(--ease-out);
	}
	h2 {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--text-md);
		margin: 0 0 var(--space-4);
	}
	ul {
		list-style: none;
		margin: 0 0 var(--space-4);
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}
	li {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--text-sm);
		color: var(--text-secondary);
	}
	kbd {
		flex-shrink: 0;
		min-width: 88px;
		text-align: center;
		font-size: var(--text-xs);
		font-weight: 700;
		color: var(--text-primary);
		border: 1px solid var(--border);
		border-radius: var(--radius-sm);
		padding: 2px 6px;
		background: var(--surface-1);
	}
	.close {
		font: inherit;
		font-weight: 600;
		font-size: var(--text-sm);
		padding: var(--space-2) var(--space-3);
		border-radius: var(--radius-sm);
		border: 1px solid var(--border);
		background: var(--surface-1);
		color: var(--text-primary);
		cursor: pointer;
	}
	.close:hover {
		border-color: var(--accent);
		color: var(--accent);
	}
	@keyframes fade {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
	@keyframes rise {
		from {
			opacity: 0;
			transform: scale(0.97);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
</style>
